import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Paper,
  InputAdornment,
} from "@mui/material";
import { Edit, Add, Email, Message, Search, Send, Payments, VerifiedUser, ManageAccounts, FactCheck, Receipt } from "@mui/icons-material";
import {
  getQuotes,
  sendQuote,
  resendQuote,
  getQuoteById,
  getUpcomingOpportunities,
} from "../features/quotes/services/quoteService";
import { canSendQuoteToCustomer } from "../features/quotes/utils/quotePdfReadiness.js";
import api from "../shared/api.jsx";
import { createOrderFromQuote } from "../features/orders/services/orderService";
import { CallRequestPanel } from "../features/callRequests/components";
import QuoteStatusManager from "../features/quotes/components/QuoteStatusManager";
import PaymentProofViewer from "../features/quotes/components/PaymentProofViewer";
import DepositVerificationPanel from "../features/quotes/components/DepositVerificationPanel";
import { ItemsDisplayWrapper } from "../components/media";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../shared/toast";
import { quoteDownloadService } from "../features/quotes/services/downloadService";
import { waitForQuoteEmailDelivery } from "../features/quotes/services/waitForQuoteEmailDelivery.js";
import UpcomingOpportunitiesList from "../features/quotes/components/UpcomingOpportunitiesList";
import QuoteListFilters from "../features/quotes/components/QuoteListFilters";
import QuoteOpsStatusRibbon from "../features/quotes/components/QuoteOpsStatusRibbon";
import QuoteCard from "../features/quotes/components/QuoteCard";
import QuotesTableDesktop from "../features/quotes/components/QuotesTableDesktop";
import useDeviceType from "../features/leads/hooks/useDeviceType";
import {
  applyQuoteListFilters,
  MOVE_DATE_FILTER_OPTIONS,
  QUOTE_SORT_OPTIONS,
} from "../features/quotes/utils/moveDateUtils";
import {
  isPdfNotReadyQuote,
} from "../features/quotes/utils/quotePdfReadiness";
import {
  getQuoteOperationalBucket,
} from "../features/quotes/services/quoteOperationalStatus";
import EmailPreviewContent from "../components/EmailPreviewContent/EmailPreviewContent";
import { Drawer, Button as AntButton, message } from "antd";
import {
  CloseOutlined,
  ExpandOutlined,
  CompressOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import { RouteMapWrapper } from "../components/RouteMap";

import {
  normalizeServiceType,
  formatServiceTypeLabel,
  serviceTypeToSopLoadType,
  SERVICE_TYPE,
} from "../shared/utils/serviceTypeLanguage.js";

/** Map quote share signals to send-dialog move type (not size-based loadType alone) */
const quoteToMoveType = (quote, lead) => {
  const fromLead = normalizeServiceType(lead?.qualification?.serviceType);
  if (fromLead) return fromLead;
  if (!quote) return SERVICE_TYPE.EXCLUSIVE_LOAD;
  const fromQuote = normalizeServiceType(quote.loadType || quote.delivery?.type);
  if (fromQuote) return fromQuote;
  if (quote.quoteType === "SL") return SERVICE_TYPE.SHARED_LOAD;
  if (/-SL$/i.test(String(quote.referenceNumber || ""))) {
    return SERVICE_TYPE.SHARED_LOAD;
  }
  if (lead?.publishedTripId) return SERVICE_TYPE.SHARED_LOAD;
  return SERVICE_TYPE.EXCLUSIVE_LOAD;
};

/** Map send-dialog move type to a valid quote loadType enum value */
const moveTypeToLoadType = (moveType, existingLoadType) => {
  return serviceTypeToSopLoadType(moveType, existingLoadType);
};

const buildWhatsIncludedForMoveType = (moveType) => {
  const includedItems = [];
  if (normalizeServiceType(moveType) === SERVICE_TYPE.SHARED_LOAD) {
    includedItems.push("Shared truck (optimised routing)");
  } else {
    includedItems.push("Dedicated truck for your move only");
  }
  includedItems.push("Trained movers");
  includedItems.push("Full protective wrapping");
  includedItems.push("Loading and offloading");
  includedItems.push("Insurance cover during transit");
  return includedItems.join("\n");
};

const Quotes = () => {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, permissions } = useAuth();
  const toast = useToast();

  const [quotes, setQuotes] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [opportunitiesLoading, setOpportunitiesLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [listSurface, setListSurface] = useState("queue"); // "queue" | "opportunities"
  const [opsBucketFilter, setOpsBucketFilter] = useState(null); // null = All
  const [estimatedCost, setEstimatedCost] = useState("");
  const [serviceType, setServiceType] = useState("standard");
  const [notes, setNotes] = useState("");
  const [moveType, setMoveType] = useState("exclusive_load");
  const [depositRequired, setDepositRequired] = useState("");
  const [depositTouched, setDepositTouched] = useState(false);
  const [depositResetRequested, setDepositResetRequested] = useState(false);
  const [depositOverrideReason, setDepositOverrideReason] = useState("");
  const [moveDate, setMoveDate] = useState("");
  const [whatsIncluded, setWhatsIncluded] = useState("");
  const [addons, setAddons] = useState("");
  const [alert, setAlert] = useState({
    show: false,
    message: "",
    severity: "success",
  });
  const [costError, setCostError] = useState("");
  const [formattedCost, setFormattedCost] = useState("");
  const [isCostFocused, setIsCostFocused] = useState(false);
  const [sendingQuote, setSendingQuote] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [emailPreview, setEmailPreview] = useState({
    subject: "",
    html: "",
    loading: false,
    error: null,
  });
  const [previewTab, setPreviewTab] = useState(0); // 0 = form, 1 = preview
  const [quoteDetailsTab, setQuoteDetailsTab] = useState(0); // 0 = payment, 1 = verification, 2 = management
  const [verifyOpenNonce, setVerifyOpenNonce] = useState(0);
  const [searchKeyword, setSearchKeyword] = useState(""); // Search keyword state
  const [moveDateFilter, setMoveDateFilter] = useState(
    MOVE_DATE_FILTER_OPTIONS.ALL
  );
  const [quoteSort, setQuoteSort] = useState(QUOTE_SORT_OPTIONS.NEWEST);
  const [mapRoute, setMapRoute] = useState(null);
  const [mapDrawerVisible, setMapDrawerVisible] = useState(false);
  const [mapDrawerExpanded, setMapDrawerExpanded] = useState(false);
  const [selectedQuoteIds, setSelectedQuoteIds] = useState(() => new Set());
  const [bulkSending, setBulkSending] = useState(false);

  // Responsive device detection
  const { isMobile, isTablet } = useDeviceType();

  // Define showAlert first to avoid circular dependency
  const showAlert = useCallback((message, severity) => {
    setAlert({ show: true, message, severity });
    setTimeout(
      () => setAlert({ show: false, message: "", severity: "success" }),
      5000
    );
  }, []);

  const showOpportunities = listSurface === "opportunities";
  const isReadyToSendBucket = opsBucketFilter === "ready_to_send";
  const isDraftBucket = opsBucketFilter === "draft";

  // Fetch all quotes (no status filter - filtering happens client-side by ops bucket)
  const fetchQuotes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getQuotes({});
      if (response.success) {
        const quotesData = response.data || response.quotes || [];
        setQuotes(quotesData);
      }
    } catch (error) {
      console.error("Error fetching quotes:", error);
      showAlert("Error fetching quotes", "error");
    } finally {
      setLoading(false);
    }
  }, [showAlert]);

  const fetchOpportunities = useCallback(async () => {
    try {
      setOpportunitiesLoading(true);
      const response = await getUpcomingOpportunities();
      if (response.success) {
        setOpportunities(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching upcoming opportunities:", error);
      showAlert("Error fetching upcoming opportunities", "error");
    } finally {
      setOpportunitiesLoading(false);
    }
  }, [showAlert]);

  // Filter quotes by ops ribbon bucket (null = All)
  const filteredQuotes = useMemo(() => {
    if (!opsBucketFilter) return quotes;
    return quotes.filter(
      (quote) => getQuoteOperationalBucket(quote) === opsBucketFilter
    );
  }, [quotes, opsBucketFilter]);

  // Search function to filter quotes across all fields and ALL categories
  const searchQuotes = useCallback((quotesList, keyword) => {
    if (!keyword || keyword.trim() === "") {
      return quotesList;
    }

    const searchTerm = keyword.toLowerCase().trim();

    return quotesList.filter((quote) => {
      // Search in quote ID
      if (quote.id?.toLowerCase().includes(searchTerm)) return true;

      // Search in reference number
      if (quote.referenceNumber?.toLowerCase().includes(searchTerm))
        return true;

      // Search in customer name
      if (quote.customerName?.toLowerCase().includes(searchTerm)) return true;

      // Search in customer phone
      if (quote.customerPhone?.toLowerCase().includes(searchTerm)) return true;

      // Search in customer email
      if (quote.customerEmail?.toLowerCase().includes(searchTerm)) return true;

      // Search in from location
      if (quote.from?.toLowerCase().includes(searchTerm)) return true;

      // Search in to location
      if (quote.to?.toLowerCase().includes(searchTerm)) return true;

      // Search in status
      if (quote.status?.toLowerCase().includes(searchTerm)) return true;

      // Search in notes
      if (quote.notes?.toLowerCase().includes(searchTerm)) return true;

      // Search in estimated cost (convert to string)
      if (quote.estimatedCost?.toString().includes(searchTerm)) return true;

      // Search in total cost (convert to string)
      if (quote.totalCost?.toString().includes(searchTerm)) return true;

      // Search in dates array
      if (quote.dates?.some((date) => date?.toLowerCase().includes(searchTerm)))
        return true;

      // Search in single date field (backward compatibility)
      if (quote.date?.toLowerCase().includes(searchTerm)) return true;

      return false;
    });
  }, []);

  // Combined filtered, searched, and sorted quotes
  const { displayedQuotes, listBaseCount } = useMemo(() => {
    const baseQuotes =
      searchKeyword && searchKeyword.trim() !== ""
        ? searchQuotes(quotes, searchKeyword)
        : filteredQuotes;

    const filtered = applyQuoteListFilters(baseQuotes, {
      moveDateFilter,
      sort: quoteSort,
    });

    return {
      displayedQuotes: filtered,
      listBaseCount: baseQuotes.length,
    };
  }, [
    quotes,
    filteredQuotes,
    searchKeyword,
    searchQuotes,
    moveDateFilter,
    quoteSort,
  ]);

  // Fetch quotes on mount and when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchQuotes();
      fetchOpportunities();
    }
  }, [isAuthenticated, fetchQuotes, fetchOpportunities]);

  useEffect(() => {
    if (isAuthenticated && showOpportunities) {
      fetchOpportunities();
    }
  }, [isAuthenticated, showOpportunities, fetchOpportunities]);

  useEffect(() => {
    setSelectedQuoteIds(new Set());
  }, [opsBucketFilter, listSurface]);

  useEffect(() => {
    if (!isDraftBucket || !isAuthenticated) return undefined;

    const hasIncompletePdfs = quotes.some(isPdfNotReadyQuote);
    if (!hasIncompletePdfs) return undefined;

    const intervalId = setInterval(() => {
      fetchQuotes();
    }, 15000);

    return () => clearInterval(intervalId);
  }, [isDraftBucket, quotes, isAuthenticated, fetchQuotes]);

  const handleQuoteFromUrl = useCallback(
    async (id) => {
      try {
        setLoading(true);
        // First try to find the quote in the existing quotes list
        let quote = quotes.find((q) => q.id === id);

        if (!quote) {
          // If not found in list, try to fetch it from the API
          const response = await getQuoteById(id);
          if (response.success) {
            quote = response.data;
          }
        }

        if (quote) {
          setSelectedQuote(quote);
          setViewDialogOpen(true);
        } else {
          showAlert("Quote not found", "error");
          // Redirect back to quotes list
          navigate("/quotes");
        }
      } catch (error) {
        console.error("Error fetching quote:", error);
        showAlert("Error fetching quote", "error");
        navigate("/quotes");
      } finally {
        setLoading(false);
      }
    },
    [quotes, showAlert, navigate]
  );

  // Handle quote ID from URL parameter - now handleQuoteFromUrl is defined
  useEffect(() => {
    if (quoteId && isAuthenticated) {
      handleQuoteFromUrl(quoteId);
    }
  }, [quoteId, isAuthenticated, handleQuoteFromUrl]);

  const handleCloseQuoteModal = () => {
    setViewDialogOpen(false);
    setSelectedQuote(null);
    setQuoteDetailsTab(0);
    setVerifyOpenNonce(0);
    // Clear URL parameter if it exists
    if (quoteId) {
      navigate("/quotes");
    }
  };

  const handleViewQuote = (quote) => {
    // Navigate to quote details page instead of opening modal
    navigate(`/quotes/${quote.id}`, {
      state: { quote }, // Pass quote data for optimistic update
    });
  };

  const handleWhatsApp = (_quote) => {
    // TODO: Implement WhatsApp action
    showAlert("WhatsApp action not yet implemented", "info");
  };

  const handlePrimaryAction = (quote, action) => {
    switch (action) {
      case "verify":
        // Open quote details on the Verification tab and launch Verify Deposit
        navigate(`/quotes/${quote.id}`, {
          state: { quote, openVerification: true },
        });
        break;
      case "respond":
        // TODO: Navigate to call request response
        showAlert("Call request response not yet implemented", "info");
        break;
      case "followup":
      case "follow_up":
        // TODO: Navigate to follow-up
        showAlert("Follow-up action not yet implemented", "info");
        break;
      default:
        handleViewQuote(quote);
        break;
    }
  };

  const resolveRequiredDeposit = (quote) => {
    const fromApi = Number(quote?.deposit?.requiredAmount);
    if (Number.isFinite(fromApi) && fromApi >= 0) return fromApi;
    if (quote?.payment?.requiredAmountOverridden === true) {
      const overridden = Number(quote?.payment?.requiredAmount);
      if (Number.isFinite(overridden) && overridden >= 0) return overridden;
    }
    const fromSop = Number(quote?.sop2Messaging?.depositAmount);
    return Number.isFinite(fromSop) && fromSop >= 0 ? fromSop : 0;
  };

  const initializeDepositEditor = (quote) => {
    setDepositRequired(String(resolveRequiredDeposit(quote)));
    setDepositTouched(false);
    setDepositResetRequested(false);
    setDepositOverrideReason("");
  };

  const handleSendQuote = (quote) => {
    setSelectedQuote(quote);
    const initialCost = quote.estimatedCost || "";
    setEstimatedCost(initialCost);
    setFormattedCost(formatDisplayValue(initialCost));
    setCostError("");
    setServiceType("standard");
    setNotes("");
    setMoveType(quoteToMoveType(quote));
    initializeDepositEditor(quote);
    setMoveDate(quote.date || quote.dates?.[0] || "");
    // Initialize What's Included from quote
    setWhatsIncluded(buildWhatsIncludedForMoveType(quoteToMoveType(quote)));
    // Initialize Addons
    setAddons(
      "Bubble wrap: R15 per metre — ideal for TVs, mirrors, glass items, artwork, delicate edges\nStandard boxes: R45 each — suitable for clothing, pantry items, kitchenware, books, linen\nBox + packing labour: R70 per box — our team packs the items for you using bubble wrap and protective materials"
    );
    setSendDialogOpen(true);
    setIsResending(false);
    setPreviewTab(0); // Reset to form tab
  };

  const handleResendQuote = (quote) => {
    setSelectedQuote(quote);
    const initialCost = quote.estimatedCost || "";
    setEstimatedCost(initialCost);
    setFormattedCost(formatDisplayValue(initialCost));
    setCostError("");
    setServiceType("standard");
    setNotes("");
    setMoveType(quoteToMoveType(quote));
    initializeDepositEditor(quote);
    setMoveDate(quote.date || quote.dates?.[0] || "");
    // Initialize What's Included from quote
    setWhatsIncluded(buildWhatsIncludedForMoveType(quoteToMoveType(quote)));
    // Initialize Addons
    setAddons(
      "Bubble wrap: R15 per metre — ideal for TVs, mirrors, glass items, artwork, delicate edges\nStandard boxes: R45 each — suitable for clothing, pantry items, kitchenware, books, linen\nBox + packing labour: R70 per box — our team packs the items for you using bubble wrap and protective materials"
    );
    setSendDialogOpen(true);
    setIsResending(true);
    setPreviewTab(0); // Reset to form tab
  };

  // Function to generate email preview
  const generateEmailPreview = useCallback(async () => {
    if (!selectedQuote) return;

    setEmailPreview((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const cleanCost = String(estimatedCost || "").replace(/,/g, "");
      const cost = cleanCost
        ? parseFloat(cleanCost)
        : selectedQuote.estimatedCost || 0;

      const params = new URLSearchParams({
        estimatedCost: cost.toString(),
        moveType: moveType,
        depositRequired: depositRequired || "",
        moveDate: moveDate || "",
        whatsIncluded: whatsIncluded || "",
        addons: addons || "",
      });

      const response = await api.get(
        `/quotes/send/${selectedQuote.id}/preview-email?${params.toString()}`
      );

      if (response.success && response.data) {
        setEmailPreview({
          subject: response.data.subject,
          html: response.data.html,
          loading: false,
          error: null,
        });
      } else {
        setEmailPreview({
          subject: "",
          html: "",
          loading: false,
          error: response.error || "Failed to generate preview",
        });
      }
    } catch (error) {
      console.error("Error generating email preview:", error);
      setEmailPreview({
        subject: "",
        html: "",
        loading: false,
        error:
          error.message ||
          "Failed to load email preview. Please check your connection.",
      });
    }
  }, [
    selectedQuote,
    estimatedCost,
    moveType,
    depositRequired,
    moveDate,
    whatsIncluded,
    addons,
  ]);

  // Generate preview when dialog opens or fields change
  useEffect(() => {
    if (sendDialogOpen && selectedQuote) {
      generateEmailPreview();
    }
  }, [
    sendDialogOpen,
    selectedQuote,
    estimatedCost,
    moveType,
    depositRequired,
    moveDate,
    whatsIncluded,
    addons,
    generateEmailPreview,
  ]);

  const executeSendQuote = async () => {
    try {
      setSendingQuote(true);
      // Validate cost before sending
      const costValidationError = validateCost(estimatedCost);
      if (costValidationError) {
        setCostError(costValidationError);
        showAlert(`Validation error: ${costValidationError}`, "error");
        setSendingQuote(false);
        return;
      }

      const cleanCost = String(estimatedCost || "").replace(/,/g, "");
      const cleanDeposit = String(depositRequired ?? "").replace(/,/g, "");
      const parsedCost = parseFloat(cleanCost);
      const parsedDeposit = Number(cleanDeposit);
      const addOnsCost = Number(
        selectedQuote?.protection?.totalAddOnsCost ??
          selectedQuote?.addOnsCost ??
          0
      );
      const parsedTotal = parsedCost + addOnsCost;

      if (
        cleanDeposit.trim() === "" ||
        !Number.isFinite(parsedDeposit) ||
        parsedDeposit < 0 ||
        parsedDeposit > parsedTotal
      ) {
        showAlert(
          `Deposit must be between R0 and R${parsedTotal.toLocaleString("en-ZA")}.`,
          "error"
        );
        setSendingQuote(false);
        return;
      }

      if (
        depositTouched &&
        !depositResetRequested &&
        depositOverrideReason.trim().length < 3
      ) {
        showAlert("Enter a reason for the deposit override.", "error");
        setSendingQuote(false);
        return;
      }

      const resolvedLoadType = moveTypeToLoadType(
        moveType,
        selectedQuote.loadType
      );

      // Update quote with form values before sending
      try {
        const contentUpdates = {
          estimatedCost: parsedCost,
          loadType: resolvedLoadType,
          date: moveDate,
        };
        if (depositResetRequested) {
          contentUpdates.depositOverrideAmount = null;
        } else if (depositTouched) {
          contentUpdates.depositOverrideAmount = parsedDeposit;
          contentUpdates.depositOverrideReason =
            depositOverrideReason.trim();
        }
        if (selectedQuote.dates && Array.isArray(selectedQuote.dates)) {
          contentUpdates.dates = [moveDate].filter(Boolean);
        }

        const updateResponse = await api.patch(
          `/quotes/update/${selectedQuote.id}/content`,
          {
            contentUpdates,
            reason: isResending
              ? "Quote resend with updates"
              : "Quote send with updates",
          }
        );

        if (updateResponse.success === false) {
          throw new Error(updateResponse.error || "Failed to update quote");
        }
      } catch (updateError) {
        console.error("Error updating quote:", updateError);
        showAlert(
          updateError.message ||
            "Failed to save quote details before sending. Please try again.",
          "error"
        );
        setSendingQuote(false);
        return;
      }

      const response = isResending
        ? await resendQuote(selectedQuote.id, {
            message: "",
            estimatedCost: parseFloat(cleanCost),
            loadType: moveType,
            serviceType,
            notes,
            fullResend: true,
          })
        : await sendQuote(selectedQuote.id, {
            message: "",
            estimatedCost: parseFloat(cleanCost),
            loadType: moveType,
            serviceType,
            notes,
          });

      if (response.success) {
        setSendDialogOpen(false);
        setPreviewTab(0);
        const quoteId = selectedQuote.id;
        fetchQuotes();
        setSendingQuote(false);

        // Undo / scheduled send (WhatsApp enabled path) — do not poll email yet
        if (response.queued && response.undoUntil) {
          toast.info(
            `Send scheduled for ${new Date(response.undoUntil).toLocaleTimeString()}. Open the quote to cancel if needed.`,
            { duration: 8 }
          );
          return;
        }

        const startedAt = new Date().toISOString();
        const toastKey = `quote-email-${quoteId}-${startedAt}`;
        toast.info(
          isResending ? "Resending quote email…" : "Sending quote email…",
          {
            key: toastKey,
            duration: 3,
            description: "PDFs will be regenerated and emailed shortly.",
          }
        );

        waitForQuoteEmailDelivery(quoteId, {
          startedAt,
          campaignId: response.campaignId || null,
        }).then((outcome) => {
          if (outcome.success) {
            toast.success("Quote email sent", { key: toastKey });
            fetchQuotes();
          } else {
            toast.error(outcome.error || "Quote email failed", {
              sticky: true,
              href: `/quotes/${quoteId}`,
              linkLabel: "Open quote",
              key: toastKey,
            });
            fetchQuotes();
          }
        });
        return;
      } else {
        toast.error(
          `Error ${isResending ? "resending" : "sending"} quote: ${response.error}`,
          {
            sticky: true,
            href: `/quotes/${selectedQuote.id}`,
            linkLabel: "Open quote",
          }
        );
      }
      setSendingQuote(false);
    } catch (error) {
      console.error("Error sending quote:", error);
      toast.error("Error sending quote", {
        sticky: true,
        href: selectedQuote?.id ? `/quotes/${selectedQuote.id}` : undefined,
        linkLabel: "Open quote",
      });
      setSendingQuote(false);
    }
  };

  const handleSendQuoteEmail = useCallback(
    async (quote) => {
      if (!quote || !quote.customerEmail) {
        toast.error("Customer email is required to send the quote by email", {
          sticky: true,
          href: quote?.id ? `/quotes/${quote.id}` : undefined,
          linkLabel: "Open quote",
        });
        return;
      }

      const startedAt = new Date().toISOString();
      const toastKey = `quote-email-${quote.id}-${startedAt}`;
      toast.info("Sending quote email…", {
        key: toastKey,
        duration: 3,
        description: "PDFs will be regenerated and emailed shortly.",
      });

      try {
        const response = await sendQuote(quote.id, {
          message: "",
          estimatedCost: quote.estimatedCost || quote.totalCost,
          serviceType: quote.serviceType || "standard",
          notes: "",
        });

        if (!response.success) {
          toast.error(
            `Error sending quote email: ${response.error}`,
            {
              sticky: true,
              href: `/quotes/${quote.id}`,
              linkLabel: "Open quote",
              key: toastKey,
            }
          );
          return;
        }

        fetchQuotes();
        const outcome = await waitForQuoteEmailDelivery(quote.id, {
          startedAt,
          campaignId: response.campaignId || null,
        });
        if (outcome.success) {
          toast.success("Quote email sent", { key: toastKey });
          fetchQuotes();
        } else {
          toast.error(outcome.error || "Quote email failed", {
            sticky: true,
            href: `/quotes/${quote.id}`,
            linkLabel: "Open quote",
            key: toastKey,
          });
        }
      } catch (error) {
        console.error("Error sending quote email:", error);
        toast.error("Error sending quote email", {
          sticky: true,
          href: `/quotes/${quote.id}`,
          linkLabel: "Open quote",
          key: toastKey,
        });
      }
    },
    [fetchQuotes, toast]
  );

  const handleResendQuoteEmail = useCallback(
    async (quote) => {
      if (!quote || !quote.customerEmail) {
        toast.error("Customer email is required to resend the quote by email", {
          sticky: true,
          href: quote?.id ? `/quotes/${quote.id}` : undefined,
          linkLabel: "Open quote",
        });
        return;
      }

      const startedAt = new Date().toISOString();
      const toastKey = `quote-email-${quote.id}-${startedAt}`;
      toast.info("Resending quote email…", {
        key: toastKey,
        duration: 3,
      });

      try {
        const response = await resendQuote(quote.id, {
          message: "",
          estimatedCost: quote.estimatedCost || quote.totalCost,
          serviceType: quote.serviceType || "standard",
          fullResend: true,
          notes: "",
        });

        if (!response.success) {
          toast.error(
            `Error resending quote email: ${response.error}`,
            {
              sticky: true,
              href: `/quotes/${quote.id}`,
              linkLabel: "Open quote",
              key: toastKey,
            }
          );
          return;
        }

        fetchQuotes();
        const outcome = await waitForQuoteEmailDelivery(quote.id, {
          startedAt,
          campaignId: response.campaignId || null,
        });
        if (outcome.success) {
          toast.success("Quote email resent", { key: toastKey });
          fetchQuotes();
        } else {
          toast.error(outcome.error || "Quote email failed", {
            sticky: true,
            href: `/quotes/${quote.id}`,
            linkLabel: "Open quote",
            key: toastKey,
          });
        }
      } catch (error) {
        console.error("Error resending quote email:", error);
        toast.error("Error resending quote email", {
          sticky: true,
          href: `/quotes/${quote.id}`,
          linkLabel: "Open quote",
          key: toastKey,
        });
      }
    },
    [fetchQuotes, toast]
  );

  const handleToggleQuoteSelection = useCallback((quoteId) => {
    setSelectedQuoteIds((prev) => {
      const next = new Set(prev);
      if (next.has(quoteId)) {
        next.delete(quoteId);
      } else {
        next.add(quoteId);
      }
      return next;
    });
  }, []);

  const handleToggleSelectAll = useCallback(
    (checked) => {
      if (!checked) {
        setSelectedQuoteIds(new Set());
        return;
      }
      setSelectedQuoteIds(new Set(displayedQuotes.map((quote) => quote.id)));
    },
    [displayedQuotes]
  );

  const handleBulkSendSelected = useCallback(async () => {
    const selectedQuotes = displayedQuotes.filter((quote) =>
      selectedQuoteIds.has(quote.id)
    );

    if (selectedQuotes.length === 0) {
      toast.info("Select at least one quote to send");
      return;
    }

    setBulkSending(true);
    let successCount = 0;
    let failCount = 0;
    const errors = [];

    for (const quote of selectedQuotes) {
      if (!quote.customerEmail) {
        failCount += 1;
        errors.push(`${quote.id}: customer email missing`);
        continue;
      }

      if (!canSendQuoteToCustomer(quote)) {
        failCount += 1;
        errors.push(
          `${quote.id}: ${
            quote.automatedPreparation
              ? "Ready to Send approval required"
              : "not ready to send"
          }`
        );
        continue;
      }

      try {
        const response = await sendQuote(quote.id, {
          message: "",
          estimatedCost: quote.estimatedCost || quote.totalCost,
          serviceType: quote.serviceType || "standard",
          notes: "",
        });

        if (response.success) {
          successCount += 1;
        } else {
          failCount += 1;
          errors.push(`${quote.id}: ${response.error || "send failed"}`);
        }
      } catch (error) {
        failCount += 1;
        errors.push(`${quote.id}: ${error.message || "send failed"}`);
      }
    }

    setBulkSending(false);
    setSelectedQuoteIds(new Set());
    await fetchQuotes();

    if (successCount > 0 && failCount === 0) {
      toast.success(
        successCount === 1
          ? "Quote email queued successfully."
          : `${successCount} quote emails queued.`
      );
    } else if (successCount > 0) {
      toast.error(
        `Queued ${successCount}, failed ${failCount}. ${errors.slice(0, 2).join("; ")}`,
        { sticky: true }
      );
    } else {
      toast.error(errors[0] || "Failed to send selected quotes", {
        sticky: true,
      });
    }
  }, [displayedQuotes, selectedQuoteIds, fetchQuotes, toast]);

  const handleGeneratePdfForQuote = useCallback(async () => {
    toast.info("PDFs are generated automatically when you send a quote email.");
  }, [toast]);

  const handleGenerateAllPendingPdfs = useCallback(async () => {
    toast.info("PDFs are generated automatically when you send a quote email.");
  }, [toast]);

  const handleDownloadQuote = useCallback(
    async (quote) => {
      try {
        await quoteDownloadService.downloadPdf(quote.id);
        showAlert("Quote PDF download started successfully", "success");
      } catch (error) {
        console.error("Error downloading quote PDF:", error);
        showAlert(error.message || "Failed to download quote PDF", "error");
      }
    },
    [showAlert]
  );

  const handleDownloadItemsList = useCallback(
    async (quote) => {
      try {
        await quoteDownloadService.downloadItemsList(quote.id);
        showAlert("Items list download started successfully", "success");
      } catch (error) {
        console.error("Error downloading items list:", error);
        showAlert(error.message || "Failed to download items list", "error");
      }
    },
    [showAlert]
  );

  const handleCreateOrderFromQuote = async (quote) => {
    try {
      const response = await createOrderFromQuote({
        quoteId: quote.id,
        notes: "Order created from accepted quote via frontend",
      });

      if (response.success) {
        fetchQuotes();
        showAlert("Order created successfully!", "success");
      } else {
        console.error("Order creation failed:", response.error);
        showAlert(`Error creating order: ${response.error}`, "error");
      }
    } catch (error) {
      console.error("Error creating order:", error);
      showAlert("Error creating order", "error");
    }
  };

  const handleEditQuote = (quote) => {
    if (!quote?.id) return;
    navigate(`/quotes/${quote.id}/edit`);
  };

  // Handle route click - open map drawer
  const handleRouteClick = (quote, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    const pickup = quote?.pickupAddress?.address || quote.from || null;
    const delivery = quote?.deliveryAddress?.address || quote.to || null;

    if (pickup && delivery) {
      setMapRoute({
        pickup,
        delivery,
        pickupAddress: quote?.pickupAddress || null,
        deliveryAddress: quote?.deliveryAddress || null,
      });
      setMapDrawerVisible(true);
    } else {
      message.warning("Pickup or delivery address is missing");
    }
  };

  // Generate Google Maps URL for route
  const getGoogleMapsUrl = (pickup, delivery) => {
    if (!pickup || !delivery) return null;
    const encodedPickup = encodeURIComponent(pickup);
    const encodedDelivery = encodeURIComponent(delivery);
    return `https://www.google.com/maps/dir/?api=1&origin=${encodedPickup}&destination=${encodedDelivery}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleEstimatedCostChange = (newCost) => {
    setEstimatedCost(newCost);
    setFormattedCost(formatDisplayValue(newCost));
    setCostError("");
    // Update deposit when price changes
    const cleanCost = String(newCost || "").replace(/,/g, "");
    const cost = cleanCost ? parseFloat(cleanCost) : 0;
    if (
      cost > 0 &&
      !depositTouched &&
      (selectedQuote?.payment?.requiredAmountOverridden !== true ||
        depositResetRequested)
    ) {
      const configuredPercent = Number(
        selectedQuote?.deposit?.minimumPercent ?? 20
      );
      setDepositRequired(
        Math.round(cost * (configuredPercent / 100)).toString()
      );
    }
  };

  const handleCostBlur = () => {
    setIsCostFocused(false);
    const validationError = validateCost(estimatedCost);
    if (validationError) {
      setCostError(validationError);
    }
  };

  const handleCostFocus = () => {
    setIsCostFocused(true);
    setCostError("");
  };

  const validateCost = (value) => {
    // Convert to string if it's a number
    const stringValue = String(value || "");

    if (!stringValue || stringValue.trim() === "") {
      return "Cost is required";
    }

    const cleanValue = stringValue.replace(/,/g, "");
    const numValue = parseFloat(cleanValue);

    if (isNaN(numValue)) {
      return "Please enter a valid number";
    }

    if (numValue <= 0) {
      return "Cost must be greater than 0";
    }

    if (numValue > 1000000) {
      return "Cost cannot exceed R1,000,000";
    }

    return "";
  };

  const formatCurrency = (value) => {
    if (!value) return "";

    const numValue =
      typeof value === "string" ? parseFloat(value.replace(/,/g, "")) : value;
    if (isNaN(numValue)) return value;

    return numValue.toLocaleString("en-ZA", {
      style: "currency",
      currency: "ZAR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  const formatDisplayValue = (value) => {
    if (!value) return "";

    const numValue =
      typeof value === "string" ? parseFloat(value.replace(/,/g, "")) : value;
    if (isNaN(numValue)) return value;

    return numValue.toLocaleString("en-ZA");
  };

  if (!isAuthenticated) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
        flexDirection="column"
        gap={2}
      >
        <Typography variant="h6" color="text.secondary">
          Please log in to view quotes
        </Typography>
        <Button variant="contained" onClick={() => navigate("/login")}>
          Go to Login
        </Button>
      </Box>
    );
  }

  // Page structure shows immediately - loading state is handled by child components
  return (
    <Box p={3}>
      {alert.show && (
        <Alert
          severity={alert.severity}
          onClose={() =>
            setAlert({ show: false, message: "", severity: "success" })
          }
          sx={{ mb: 2 }}
        >
          {alert.message}
        </Alert>
      )}

      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography variant="h4" component="h1">
            Quotes
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              onClick={() => {
                if (showOpportunities) {
                  setListSurface("queue");
                } else {
                  setListSurface("opportunities");
                  fetchOpportunities();
                }
              }}
            >
              {showOpportunities
                ? "Back to Quote queue"
                : "Upcoming Opportunities"}
            </Button>
            {permissions?.includes("quotes:create") && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<Add />}
                onClick={() => navigate("/quotes/new")}
                sx={{ minWidth: 140 }}
              >
                Create Quote
              </Button>
            )}
          </Box>
        </Box>

        {/* Search Bar */}
        <TextField
          fullWidth
          placeholder="Search quotes across all categories by ID, customer name, phone, location, status, cost..."
          value={searchKeyword}
          onChange={(e) => setSearchKeyword(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />

        {!showOpportunities && (
          <>
            <QuoteOpsStatusRibbon
              quotes={quotes}
              activeBucket={opsBucketFilter}
              onBucketClick={setOpsBucketFilter}
            />
            <QuoteListFilters
              moveDateFilter={moveDateFilter}
              onMoveDateFilterChange={setMoveDateFilter}
              sort={quoteSort}
              onSortChange={setQuoteSort}
              resultCount={displayedQuotes.length}
              totalCount={listBaseCount}
            />
          </>
        )}
      </Box>

      {isReadyToSendBucket && displayedQuotes.length > 0 && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            borderRadius: 2,
            bgcolor: "grey.50",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {selectedQuoteIds.size > 0
              ? `${selectedQuoteIds.size} quote${selectedQuoteIds.size === 1 ? "" : "s"} selected`
              : "Select quotes with ready PDFs, then send in bulk"}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              onClick={() => setSelectedQuoteIds(new Set())}
              disabled={selectedQuoteIds.size === 0 || bulkSending}
            >
              Clear selection
            </Button>
            {permissions?.includes("quotes:send") && (
              <Button
                variant="contained"
                startIcon={bulkSending ? <CircularProgress size={18} /> : <Send />}
                disabled={selectedQuoteIds.size === 0 || bulkSending}
                onClick={handleBulkSendSelected}
              >
                {bulkSending
                  ? "Sending…"
                  : `Send selected (${selectedQuoteIds.size})`}
              </Button>
            )}
          </Box>
        </Box>
      )}

      {isDraftBucket && displayedQuotes.length > 0 && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            borderRadius: 2,
            bgcolor: "grey.50",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            These pending quotes cannot be sent until their PDF is ready. The
            list refreshes automatically while PDFs are generating.
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button variant="outlined" onClick={fetchQuotes}>
              Refresh
            </Button>
          </Box>
        </Box>
      )}

      {/* Conditional View Rendering by Device Type */}
      {showOpportunities ? (
        <UpcomingOpportunitiesList
          opportunities={opportunities}
          loading={opportunitiesLoading}
        />
      ) : loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : isMobile ? (
        // Mobile: Cards only
        <Box>
          {displayedQuotes.length === 0 ? (
            <Box sx={{ textAlign: "center", p: 4 }}>
              <Typography variant="body1" color="text.secondary">
                {searchKeyword || moveDateFilter !== MOVE_DATE_FILTER_OPTIONS.ALL
                  ? "No quotes found matching your filters"
                  : "No quotes found in this queue"}
              </Typography>
            </Box>
          ) : (
            displayedQuotes.map((quote) => (
              <QuoteCard
                key={quote.id}
                quote={quote}
                onView={handleViewQuote}
                onWhatsApp={handleWhatsApp}
                onPrimaryAction={handlePrimaryAction}
                onSendEmail={handleSendQuoteEmail}
                onResendEmail={handleResendQuoteEmail}
                onRouteClick={handleRouteClick}
                selectionMode={isReadyToSendBucket}
                isSelected={selectedQuoteIds.has(quote.id)}
                onToggleSelection={handleToggleQuoteSelection}
                showPdfStatus={isDraftBucket}
                onSendQuote={handleSendQuote}
              />
            ))
          )}
        </Box>
      ) : isTablet ? (
        // Tablet: Dense card grid
        <Grid container spacing={2}>
          {displayedQuotes.length === 0 ? (
            <Grid item xs={12}>
              <Box sx={{ textAlign: "center", p: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  {searchKeyword || moveDateFilter !== MOVE_DATE_FILTER_OPTIONS.ALL
                    ? "No quotes found matching your filters"
                    : "No quotes found in this queue"}
                </Typography>
              </Box>
            </Grid>
          ) : (
            displayedQuotes.map((quote) => (
              <Grid item xs={12} sm={6} key={quote.id}>
                <QuoteCard
                  quote={quote}
                  onView={handleViewQuote}
                  onWhatsApp={handleWhatsApp}
                  onPrimaryAction={handlePrimaryAction}
                  onSendEmail={handleSendQuoteEmail}
                  onResendEmail={handleResendQuoteEmail}
                  onRouteClick={handleRouteClick}
                  selectionMode={isReadyToSendBucket}
                  isSelected={selectedQuoteIds.has(quote.id)}
                  onToggleSelection={handleToggleQuoteSelection}
                  showPdfStatus={isDraftBucket}
                  onSendQuote={handleSendQuote}
                />
              </Grid>
            ))
          )}
        </Grid>
      ) : (
        // Desktop: Table + side panel
        <>
          {displayedQuotes.length === 0 ? (
            <Box sx={{ textAlign: "center", p: 4 }}>
              <Typography variant="body1" color="text.secondary">
                {searchKeyword || moveDateFilter !== MOVE_DATE_FILTER_OPTIONS.ALL
                  ? "No quotes found matching your filters"
                  : "No quotes found in this queue"}
              </Typography>
            </Box>
          ) : (
            <QuotesTableDesktop
              quotes={displayedQuotes}
              permissions={permissions}
              onRouteClick={handleRouteClick}
              onViewQuote={handleViewQuote}
              onEditQuote={handleEditQuote}
              onSendQuote={handleSendQuote}
              onResendQuote={handleResendQuote}
              onCreateOrder={handleCreateOrderFromQuote}
              onWhatsApp={handleWhatsApp}
              selectionMode={isReadyToSendBucket}
              selectedQuoteIds={selectedQuoteIds}
              onToggleQuoteSelection={handleToggleQuoteSelection}
              onToggleSelectAll={handleToggleSelectAll}
              showPdfStatus={isDraftBucket}
            />
          )}
        </>
      )}

      {/* View Quote Dialog */}
      <Dialog
        open={viewDialogOpen && !!selectedQuote}
        onClose={handleCloseQuoteModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Quote Details</DialogTitle>
        <DialogContent>
          {selectedQuote && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Customer: {selectedQuote.customerName || "Unknown"}
              </Typography>
              <Typography variant="body2" gutterBottom>
                Phone: {selectedQuote.customerPhone || "No phone"}
              </Typography>
              <Typography variant="body2" gutterBottom>
                From: {selectedQuote.from}
              </Typography>
              <Typography variant="body2" gutterBottom>
                To: {selectedQuote.to}
              </Typography>
              <Typography variant="body2" gutterBottom>
                Service Date:{" "}
                {selectedQuote.dates && selectedQuote.dates.length > 0
                  ? selectedQuote.dates[0]
                  : selectedQuote.date || "No date"}
              </Typography>
              {selectedQuote.estimatedCost && (
                <Typography variant="body2" gutterBottom>
                  Estimated Cost: {formatCurrency(selectedQuote.estimatedCost)}
                </Typography>
              )}
              <Typography variant="body2" gutterBottom>
                Status: {selectedQuote.status}
              </Typography>
              <Typography variant="body2" gutterBottom>
                Created: {formatDate(selectedQuote.createdAt)}
              </Typography>
              {selectedQuote.notes && (
                <Typography variant="body2" gutterBottom>
                  Notes: {selectedQuote.notes}
                </Typography>
              )}
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Items:
              </Typography>
              <ItemsDisplayWrapper
                items={selectedQuote.items}
                entityId={selectedQuote.id}
                entityType="quotes"
                maxItems={12}
                showSectionHeaders={false}
              />

              {/* Quick verify action — always visible when eligible */}
              {permissions.includes("quotes:update") &&
                ["accepted", "pending_payment", "pending", "sent", "approved"].includes(
                  selectedQuote.status
                ) &&
                !selectedQuote.payment?.depositVerified && (
                  <Box sx={{ mt: 2, mb: 1 }}>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<FactCheck />}
                      fullWidth
                      data-testid="quick-verify-deposit-button"
                      onClick={() => {
                        setQuoteDetailsTab(1);
                        setVerifyOpenNonce((n) => n + 1);
                      }}
                      sx={{
                        backgroundColor: "#52c41a",
                        "&:hover": { backgroundColor: "#389e0d" },
                        fontWeight: 600,
                      }}
                    >
                      Verify Deposit (SOP #3)
                    </Button>
                  </Box>
                )}

              {(selectedQuote.payment?.depositVerified ||
                selectedQuote.status === "deposit_verified") && (
                <Box sx={{ mt: 2, mb: 1 }}>
                  <Button
                    variant="outlined"
                    color="success"
                    startIcon={<Receipt />}
                    fullWidth
                    data-testid="quick-view-receipt-button"
                    onClick={() => {
                      setQuoteDetailsTab(1);
                      setVerifyOpenNonce((n) => n + 1);
                    }}
                  >
                    View Deposit Receipt
                  </Button>
                </Box>
              )}

              {/* Payment / Verification / Management Tabs */}
              <Box sx={{ mt: 3 }}>
                <Tabs
                  value={quoteDetailsTab}
                  onChange={(_, value) => setQuoteDetailsTab(value)}
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                  sx={{
                    mb: 2,
                    borderBottom: 1,
                    borderColor: "divider",
                    minHeight: 44,
                    "& .MuiTab-root": {
                      minHeight: 44,
                      textTransform: "none",
                      fontWeight: 600,
                    },
                  }}
                >
                  <Tab
                    icon={<Payments fontSize="small" />}
                    iconPosition="start"
                    label="Payment"
                  />
                  <Tab
                    icon={<VerifiedUser fontSize="small" />}
                    iconPosition="start"
                    label="Verification"
                  />
                  <Tab
                    icon={<ManageAccounts fontSize="small" />}
                    iconPosition="start"
                    label="Management"
                  />
                </Tabs>

                {quoteDetailsTab === 0 && (
                  <PaymentProofViewer quote={selectedQuote} />
                )}

                {quoteDetailsTab === 1 && (
                  <DepositVerificationPanel
                    quote={selectedQuote}
                    onUpdate={() => {
                      fetchQuotes();
                      getQuoteById(selectedQuote.id).then((response) => {
                        if (response?.success && response.data) {
                          setSelectedQuote(response.data);
                        }
                      });
                    }}
                    userRole={user?.role || "user"}
                    userId={user?.userId || user?.id}
                    canManage={permissions.includes("quotes:update")}
                    openVerifyNonce={verifyOpenNonce}
                  />
                )}

                {quoteDetailsTab === 2 && (
                  <QuoteStatusManager
                    quote={selectedQuote}
                    onUpdate={(updatedQuote) => {
                      setSelectedQuote(updatedQuote);
                      fetchQuotes();
                    }}
                    userRole={user?.role || "user"}
                    userId={user?.userId || user?.id}
                    canManage={permissions.includes("quotes:update")}
                  />
                )}
              </Box>

              {/* Call Request Panel */}
              {permissions.includes("call:view") && (
                <Box sx={{ mt: 3 }}>
                  <CallRequestPanel
                    quoteId={selectedQuote.id}
                    onUpdate={() => {
                      // Refresh quote data when call request is updated
                      fetchQuotes();
                    }}
                  />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {permissions.includes("quotes:update") && (
            <Button
              variant="outlined"
              startIcon={<Edit />}
              onClick={() => {
                handleEditQuote(selectedQuote);
                handleCloseQuoteModal();
              }}
            >
              Edit Quote
            </Button>
          )}
          <Button onClick={handleCloseQuoteModal}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Send Quote Dialog */}
      <Dialog
        open={sendDialogOpen}
        onClose={() => {
          setSendDialogOpen(false);
          setPreviewTab(0);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{isResending ? "Resend Quote" : "Send Quote"}</DialogTitle>
        <DialogContent>
          <Tabs
            value={previewTab}
            onChange={(e, newValue) => setPreviewTab(newValue)}
            sx={{ mb: 2 }}
          >
            <Tab icon={<Message />} iconPosition="start" label="Form" />
            <Tab icon={<Email />} iconPosition="start" label="Email Preview" />
          </Tabs>

          {previewTab === 0 && (
            <Box sx={{ mt: 2 }}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Move Type</InputLabel>
                <Select
                  value={moveType}
                  label="Move Type"
                  onChange={(e) => {
                    setMoveType(e.target.value);
                    setWhatsIncluded(buildWhatsIncludedForMoveType(e.target.value));
                  }}
                >
                  <MenuItem value="exclusive_load">
                    {formatServiceTypeLabel("exclusive_load")}
                  </MenuItem>
                  <MenuItem value="shared_load">
                    {formatServiceTypeLabel("shared_load")}
                  </MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Total Price (R)"
                value={isCostFocused ? estimatedCost : formattedCost}
                onChange={(e) => handleEstimatedCostChange(e.target.value)}
                onFocus={handleCostFocus}
                onBlur={handleCostBlur}
                error={!!costError}
                helperText={costError}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Deposit Required (R)"
                value={depositRequired}
                onChange={(e) => {
                  setDepositRequired(e.target.value);
                  setDepositTouched(true);
                  setDepositResetRequested(false);
                }}
                sx={{ mb: 2 }}
              />

              {depositTouched && !depositResetRequested && (
                <TextField
                  fullWidth
                  required
                  label="Deposit Override Reason"
                  value={depositOverrideReason}
                  onChange={(e) => setDepositOverrideReason(e.target.value)}
                  sx={{ mb: 2 }}
                />
              )}

              {selectedQuote?.payment?.requiredAmountOverridden === true && (
                <Button
                  type="button"
                  variant="text"
                  onClick={() => {
                    const configuredPercent = Number(
                      selectedQuote?.deposit?.minimumPercent ?? 20
                    );
                    const total =
                      Number(
                        String(estimatedCost || "0").replace(/,/g, "")
                      ) +
                      Number(
                        selectedQuote?.protection?.totalAddOnsCost ??
                          selectedQuote?.addOnsCost ??
                          0
                      );
                    setDepositRequired(
                      String(Math.round(total * (configuredPercent / 100)))
                    );
                    setDepositTouched(false);
                    setDepositResetRequested(true);
                    setDepositOverrideReason("");
                  }}
                  sx={{ mb: 2 }}
                >
                  Reset deposit to default
                </Button>
              )}

              <TextField
                fullWidth
                label="Move Date"
                type="date"
                value={moveDate}
                onChange={(e) => setMoveDate(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="What's Included"
                multiline
                rows={6}
                value={whatsIncluded}
                onChange={(e) => setWhatsIncluded(e.target.value)}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Optional Add-ons"
                multiline
                rows={4}
                value={addons}
                onChange={(e) => setAddons(e.target.value)}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Notes (optional)"
                multiline
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </Box>
          )}

          {previewTab === 1 && (
            <Box sx={{ mt: 2 }}>
              {emailPreview.loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : emailPreview.error ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {emailPreview.error}
                </Alert>
              ) : (
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    bgcolor: "background.paper",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Subject:
                  </Typography>
                  <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                    {emailPreview.subject || "Loading..."}
                  </Typography>

                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Email Body:
                  </Typography>
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      bgcolor: "grey.50",
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: "divider",
                      minHeight: 400,
                      maxHeight: 600,
                      overflow: "auto",
                    }}
                  >
                    <EmailPreviewContent html={emailPreview.html} />
                  </Box>

                  <Alert severity="info" sx={{ mt: 2 }}>
                    This is a preview of the email that will be sent. Any edits
                    made in the form will be reflected in the final email.
                  </Alert>
                </Paper>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setSendDialogOpen(false);
              setPreviewTab(0);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={executeSendQuote}
            variant="contained"
            color="primary"
            disabled={sendingQuote}
          >
            {sendingQuote
              ? isResending
                ? "Resending..."
                : "Sending..."
              : isResending
                ? "Resend Quote"
                : "Send Quote"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Map Drawer */}
      <Drawer
        title={
          <div>
            <div
              style={{ fontSize: "16px", fontWeight: 600, marginBottom: "4px" }}
            >
              Route Map
            </div>
            {mapRoute && (
              <div style={{ fontSize: "12px", color: "#666" }}>
                {mapRoute.pickup} → {mapRoute.delivery}
              </div>
            )}
          </div>
        }
        placement={isMobile ? "bottom" : "right"}
        width={isMobile ? "100%" : mapDrawerExpanded ? "75%" : "600px"}
        height={isMobile ? "80vh" : "100%"}
        onClose={() => {
          setMapDrawerVisible(false);
          setMapRoute(null);
          setMapDrawerExpanded(false);
        }}
        open={mapDrawerVisible}
        extra={
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            {!isMobile && (
              <AntButton
                type="text"
                icon={
                  mapDrawerExpanded ? <CompressOutlined /> : <ExpandOutlined />
                }
                onClick={() => setMapDrawerExpanded(!mapDrawerExpanded)}
                title={mapDrawerExpanded ? "Compress map" : "Expand map"}
              />
            )}
            <AntButton
              type="text"
              icon={<CloseOutlined />}
              onClick={() => {
                setMapDrawerVisible(false);
                setMapRoute(null);
                setMapDrawerExpanded(false);
              }}
            />
          </div>
        }
      >
        {mapRoute && (
          <div
            style={{
              height: isMobile ? "calc(80vh - 80px)" : "calc(100vh - 120px)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                flex: 1,
                minHeight: 0,
                borderRadius: "8px",
                overflow: "hidden",
                border: "1px solid #e8e8e8",
                backgroundColor: "#f5f5f5",
              }}
            >
              <RouteMapWrapper
                pickup={mapRoute.pickup}
                delivery={mapRoute.delivery}
                pickupAddress={mapRoute.pickupAddress}
                deliveryAddress={mapRoute.deliveryAddress}
                drawerExpanded={mapDrawerExpanded}
              />
            </div>
            <div style={{ marginTop: "16px", display: "flex", gap: "8px" }}>
              <AntButton
                type="primary"
                icon={<EnvironmentOutlined />}
                href={getGoogleMapsUrl(mapRoute.pickup, mapRoute.delivery)}
                target="_blank"
                block={isMobile}
              >
                Open Route in Google Maps
              </AntButton>
            </div>
          </div>
        )}
      </Drawer>
    </Box>
  );
};

export default Quotes;
